import { useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type { VerificationDecisionAction, VerificationType } from "../types";
import { formatLabel } from "../labels";

const VERIFICATION_TYPES: VerificationType[] = [
  "user_identity",
  "creator_eligibility",
  "business_identity",
  "business_ownership",
  "payment_eligibility",
  "role_requirement",
];

const DECISIONS: VerificationDecisionAction[] = [
  "submit",
  "start_review",
  "request_more_information",
  "verify",
  "accept",
  "reject",
  "revoke",
  "supersede",
];

export interface RequestVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { verificationType: VerificationType }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function RequestVerificationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: RequestVerificationDialogProps) {
  const [verificationType, setVerificationType] =
    useState<VerificationType>("user_identity");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Request verification"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => void onSubmit({ verificationType })}
          >
            Request
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Select
          label="Verification type"
          value={verificationType}
          onChange={(event) =>
            setVerificationType(event.target.value as VerificationType)
          }
          options={VERIFICATION_TYPES.map((type) => ({
            value: type,
            label: formatLabel(type),
          }))}
        />
        {error ? (
          <Alert tone="danger" title="Request failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export interface DecideVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: VerificationDecisionAction;
    decisionReason: string;
    approvalId?: string;
    isOverride?: boolean;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function DecideVerificationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: DecideVerificationDialogProps) {
  const [action, setAction] =
    useState<VerificationDecisionAction>("start_review");
  const [decisionReason, setDecisionReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [isOverride, setIsOverride] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Verification decision"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={decisionReason.trim().length < 3}
            onClick={() =>
              void onSubmit({
                action,
                decisionReason,
                ...(approvalId.trim()
                  ? { approvalId: approvalId.trim() }
                  : {}),
                ...(isOverride ? { isOverride: true } : {}),
              })
            }
          >
            Submit decision
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Verification override requires Dual approval when enforced by the
          backend. Account and verification states remain separate.
        </Text>
        <Select
          label="Decision action"
          value={action}
          onChange={(event) =>
            setAction(event.target.value as VerificationDecisionAction)
          }
          options={DECISIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Decision reason"
          value={decisionReason}
          onChange={(event) => setDecisionReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
        />
        <Checkbox
          label="Mark as verification override"
          checked={isOverride}
          onChange={(event) => setIsOverride(event.target.checked)}
        />
        {error ? (
          <Alert tone="danger" title="Decision failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
